/**
 * auto-answer — ITEM 99 / §9.
 *
 * Decides whether an incoming call should be answered automatically.
 *
 * WHAT EXISTED. `autoAnswerIncomingCalls` is settable in the admin UI
 * (`pages/admin/user-management/TelephonyTab.tsx:555`) and persisted by the API
 * (`routes/agents/update.ts:100`). The softphone never read it — so an admin can
 * turn the setting on, see it saved, and nothing changes. A control that
 * persists and does nothing is worse than a missing one: it is a promise the
 * system does not keep.
 *
 * A PURE FUNCTION, deliberately. The interesting part is not calling
 * `session.answer()` — it is the three cases where auto-answer must NOT fire,
 * and those are testable only if the decision is separated from the JsSIP
 * plumbing.
 */

/**
 * Matches the states `useSoftphone` actually sets. INCOMING calls are
 * `'ringing_in'` — `'ringing'` is the outbound side. Getting this wrong would
 * make auto-answer fire on the agent's own outgoing calls, or never fire at all.
 */
export type SoftphoneCallState =
  | 'idle'
  | 'ringing'
  | 'ringing_in'
  | 'in_call'
  | 'on_hold'
  | 'ended'

export interface AutoAnswerInput {
  /** From the agent's `autoAnswerIncomingCalls` profile flag. */
  enabled?: boolean | undefined
  /** Current state of the INCOMING call. */
  callState: SoftphoneCallState | string
  /** True when the agent is already on another call. */
  hasActiveCall?: boolean | undefined
}

export function shouldAutoAnswer(input: AutoAnswerInput): boolean {
  // Fail closed on a missing flag. Profiles predating this setting have it
  // undefined, and defaulting to "on" would silently start answering calls for
  // every one of those agents.
  if (input.enabled !== true) return false

  // Only an INCOMING ringing call can be answered. 'ringing_in' is what
  // useSoftphone sets for a remote-originated session (useSoftphone.ts:383);
  // 'ringing' is the outbound side, and answering that is meaningless.
  //
  // Answering a call already in_call or ended sends a second 200 OK, which
  // JsSIP rejects — surfacing as a console error on every call rather than as a
  // visible fault.
  if (input.callState !== 'ringing_in') return false

  // NEVER auto-answer while another call is up. Doing so either drops the first
  // caller into a conference they did not consent to, or cuts them off — and the
  // agent gets no chance to intervene, because the whole point of auto-answer is
  // that they were not asked.
  if (input.hasActiveCall === true) return false

  return true
}
